import { jsx, jsxs } from "react/jsx-runtime";
import { cn } from "@pcln/horizon";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import styled_components from "styled-components";
import { RealtimeVoiceButton } from "../../svgs/RealtimeIcon.js";
const StyledDiv = /*#__PURE__*/ styled_components.div.withConfig({
    displayName: "ExpandableInput__StyledDiv"
})([
    "#realtime-voice-button{svg{display:block !important;}}"
]);
function ExpandableInput({ placeholder = 'Ask Penny a question', className, id, value, onChange, maxLineHeight = 3, disabled = false, voiceIconClassName, submitIconClassName, variant = 'glow', voiceIcon, submitIcon, onSubmitClickHandler, onVoiceClickHandler, onHeightChange }) {
    const [inputValue, setInputValue] = useState(value || '');
    const textareaRef = useRef(null);
    const [isMultiLine, setIsMultiLine] = useState(false);
    const [textareaHeight, setTextareaHeight] = useState(32);
    const [isHovered, setIsHovered] = useState(false);
    const adjustHeight = ()=>{
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const scrollHeight = textarea.scrollHeight;
            const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
            const maxHeight = lineHeight * maxLineHeight;
            const hasMultipleLines = scrollHeight - 20 > lineHeight;
            setIsMultiLine(hasMultipleLines);
            let finalHeight;
            if (scrollHeight > maxHeight) {
                finalHeight = maxHeight;
                textarea.style.height = `${maxHeight}px`;
                textarea.style.overflowY = 'auto';
            } else {
                finalHeight = scrollHeight;
                textarea.style.height = `${scrollHeight}px`;
                textarea.style.overflowY = 'hidden';
            }
            onHeightChange(finalHeight);
            setTextareaHeight(finalHeight);
        }
    };
    useEffect(()=>{
        adjustHeight();
    }, [
        inputValue
    ]);
    useEffect(()=>{
        const styleId = 'subtle-gradient-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
        .subtle-glow:hover:not(:disabled),
        .subtle-glow:focus:not(:disabled) {
          outline: none;
          border: 1px solid transparent;
          background-image: linear-gradient(white, white), linear-gradient(to right, #7700BB, #5C18C7, #0068EF);
          background-origin: border-box;
          background-clip: padding-box, border-box;
          box-shadow: 0 0 4px rgba(119, 0, 187, 0.2);
        }

         .no-glow:hover:not(:disabled),
      .no-glow:focus:not(:disabled) {
        border-color: #496785 !important;
        transition: border-color 0.2s ease;
      }
      `;
            document.head.appendChild(style);
        }
    }, []);
    useEffect(()=>{
        if (textareaRef.current) textareaRef.current.focus();
    }, [
        disabled,
        textareaRef
    ]);
    const setFocusTextarea = ()=>{
        if (textareaRef.current) textareaRef.current.focus();
    };
    function handleChange(e) {
        const newValue = e.target.value;
        setInputValue(newValue);
        if (onChange) onChange(newValue);
    }
    function handleSubmit() {
        if (!onSubmitClickHandler || !inputValue.trim()) return;
        onSubmitClickHandler(inputValue);
        setInputValue('');
        setFocusTextarea();
    }
    function handleKeyDown(e) {
        if ('Enter' === e.key && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    }
    const renderVoiceButton = ()=>/*#__PURE__*/ jsx(RealtimeVoiceButton, {
            onClick: onVoiceClickHandler
        }, "voice-button");
    const renderSubmitButton = ()=>/*#__PURE__*/ jsx("div", {
            className: "relative flex h-[32px] max-h-[32px] w-[32px] max-w-[32px] -translate-x-2 translate-y-1 items-center justify-end rounded-full",
            children: /*#__PURE__*/ jsx(AnimatePresence, {
                children: /*#__PURE__*/ jsx(motion.button, {
                    type: "button",
                    initial: {
                        opacity: 0,
                        scale: 0.8
                    },
                    animate: {
                        opacity: 1,
                        scale: 1
                    },
                    exit: {
                        opacity: 0,
                        scale: 0.8
                    },
                    transition: {
                        duration: 0.075,
                        ease: 'easeOut'
                    },
                    "aria-label": "send message",
                    className: `absolute inset-0 ${submitIconClassName}`,
                    onClick: handleSubmit,
                    style: {
                        borderRadius: '50%'
                    },
                    children: submitIcon
                }, "send-button")
            })
        });
    const renderButtons = ()=>{
        if (submitIcon && voiceIcon) return 0 === inputValue.length ? renderVoiceButton() : renderSubmitButton();
        if (submitIcon && !voiceIcon) return renderSubmitButton();
        return null;
    };
    function resolvedBoderColor() {
        if ('default' === variant && isHovered) return '#496785';
        if ('glow' === variant && isHovered) return;
        return '#c0cad5';
    }
    return /*#__PURE__*/ jsxs("button", {
        className: cn('relative border transition-all', isMultiLine ? 'rounded-xl' : 'rounded-full', {
            'subtle-glow focus:outline-none focus:ring-0': 'glow' === variant
        }, {
            'no-glow focus:outline-none focus:ring-0': 'default' === variant
        }),
        style: {
            width: '100%',
            backgroundColor: 'white',
            borderColor: resolvedBoderColor()
        },
        type: 'button',
        "aria-labelledby": "container-wrapper-expandable-text",
        onMouseEnter: function() {
            setIsHovered(true);
        },
        onMouseLeave: function() {
            setIsHovered(false);
        },
        children: [
            /*#__PURE__*/ jsx("div", {
                style: {
                    display: 'flex',
                    alignItems: 'stretch'
                },
                children: /*#__PURE__*/ jsxs(StyledDiv, {
                    style: {
                        display: 'flex',
                        alignItems: 'center',
                        flex: '90%'
                    },
                    children: [
                        /*#__PURE__*/ jsx("textarea", {
                            ref: textareaRef,
                            id: id,
                            value: inputValue,
                            "aria-label": "Message input",
                            onChange: handleChange,
                            onKeyDown: handleKeyDown,
                            placeholder: placeholder,
                            disabled: disabled,
                            rows: 1,
                            className: cn('border-0 bg-transparent focus:outline-none focus:ring-0', disabled && 'cursor-not-allowed bg-slate-100', className),
                            style: {
                                resize: 'none',
                                padding: '8px 16px 0',
                                width: '100%',
                                color: '#001833'
                            }
                        }),
                        renderButtons()
                    ]
                })
            }),
            /*#__PURE__*/ jsx("div", {
                style: {
                    height: '8px'
                }
            })
        ]
    });
}
export { ExpandableInput };
